/* eslint-disable */

import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  HStack,
  Avatar,
  IconButton,
  Tooltip,
  Modal, Input, Select,
  ModalOverlay, ModalContent, ModalHeader,
  ModalCloseButton, ModalBody, ModalFooter,
  useColorModeValue,
  useDisclosure,
  Spinner,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { AddIcon, ViewIcon, ViewOffIcon, CopyIcon, InfoOutlineIcon } from "@chakra-ui/icons";

// Custom components
import Card from 'components/card/Card';
import { NavLink, useNavigate } from 'react-router-dom';
import constants from 'providers/Constants';
import { Button } from '@chakra-ui/react';
import useEndpoints from "providers/Endpoints";

const columnHelper = createColumnHelper();

export default function CardsTable(props) {
  const { t } = useTranslation();
  const { currenciesData, walletsData, tableData, sortArray, index, searchQuery } = props;
  const endpoints = useEndpoints();
  const navigate = useNavigate();

  const quantity = 1;

  const [wallets, setWallets] = React.useState(walletsData || []);
  const [{ pageIndex, pageSize }, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 5,
  });
  const [error, setError] = React.useState([]);
  const [postData, setPostData] = React.useState({});
  const [currencies, setCurrencies] = React.useState(currenciesData || []);
  const [sorting, setSorting] = React.useState([]);
  const [loading, setLoading] = useState(false);
  const [visibleMap, setVisibleMap] = React.useState({});
  const [visibleCvvMap, setVisibleCvvMap] = React.useState({});
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isActionModalOpen, onOpen: onActionModalOpen, onClose: onActionModalClose } = useDisclosure();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  // Unconditional color values to avoid calling hooks conditionally in JSX
  const bannerBg = useColorModeValue('brand.50', 'whiteAlpha.100');
  const bannerBorder = useColorModeValue('brand.100', 'whiteAlpha.200');
  const bannerAccent = useColorModeValue('brand.500', 'brand.300');
  const instructionsBodyColor = useColorModeValue('secondaryGray.700', 'gray.300');

  // helpers
  const money = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const clearPricingFields = (extra = {}) => ({
    exchange_rate: null,
    sub_total: null,
    fee: null,
    total: null,
    conversion_id: null,
    fee_id: null,
    ...extra,
  });

  // Shared inline style for native <select> elements
  const baseSelectStyle = {
    width: '100%',
    padding: '10px',
    marginBottom: '24px',
    borderRadius: '15px',
    borderColor: '#E2E8F0',
    borderWidth: '1px',
    fontSize: 'sm',
    fontWeight: '500',
  };
  // helpers (UI + formatting + clipboard)
  const headerCell = (label) => (
    <Text textAlign="center" fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
      {label}
    </Text>
  );
  const onlyDigits = (s) => String(s || '').replace(/\D+/g, '');
  const group4 = (s) => String(s || '').replace(/(.{4})/g, '$1 ').trim();
  const formatPan = (raw, show) => {
    const digits = onlyDigits(raw);
    if (!digits) return 'N/A';
    if (show) return group4(digits);
    const last4 = digits.slice(-4);
    const masked = digits.slice(0, -4).replace(/\d/g, '•');
    return group4(masked + last4);
  };
  const getRowKey = (info) => info?.row?.original?.card_id ?? info?.row?.id;
  const NAMono = () => (
    <Text color={textColor} fontSize="sm" fontWeight="700" fontFamily="monospace">{t('N/A')}</Text>
  );
  const SecureActions = ({ copyValue, isVisible, onToggle, copyLabel, toggleLabels }) => (
    <HStack spacing={1}>
      <Tooltip label={copyLabel}>
        <IconButton
          aria-label={copyLabel}
          size="xs"
          variant="ghost"
          icon={<CopyIcon />}
          onClick={(e) => {
            e.stopPropagation();
            constants.copyToClipboard(copyValue);
          }}
        />
      </Tooltip>
      <Tooltip label={isVisible ? toggleLabels.hide : toggleLabels.show}>
        <IconButton
          aria-label={isVisible ? toggleLabels.hide : toggleLabels.show}
          size="xs"
          variant="ghost"
          icon={isVisible ? <ViewOffIcon /> : <ViewIcon />}
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        />
      </Tooltip>
    </HStack>
  );
  const columns = [
    columnHelper.accessor('name_on_card', {
      id: 'name_on_card',
      header: () => headerCell(t('NAME ON CARD')),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700" noOfLines={1}>
          {info.getValue() || t('N/A')}
        </Text>
      ),
    }),
    columnHelper.accessor('type', {
      id: 'type',
      header: () => headerCell(t('CARD TYPE')),
      cell: (info) => {
        const value = info.getValue();
        const avatar = info?.row?.original?.avatar;
        return (
          <HStack spacing={2} align="center">
            {avatar ? (
              <Avatar src={avatar} name={String(value || t('Card'))} size="xs" />
            ) : null}
            <Text color={textColor} fontSize="sm" fontWeight="700" noOfLines={1}>
              {value || t('N/A')}
            </Text>
          </HStack>
        );
      },
    }),
    columnHelper.accessor('credentials.card_pan', {
      id: 'credentials.card_pan',
      header: () => headerCell(t('CARD NUMBER')),
      cell: (info) => {
        const raw = info.getValue();
        if (!raw) {
          return <NAMono />;
        }
        const rowKey = getRowKey(info);
        const isVisible = !!visibleMap[rowKey];
        const display = formatPan(raw, isVisible);
        const copyValue = onlyDigits(raw);
        return (
          <HStack justify="space-between" spacing={2} align="center" w="100%" minW={{ base: '200px', md: '200px', lg: '200px' }}>
            <Text color={textColor} fontSize="sm" fontWeight="700" fontFamily="monospace" whiteSpace="nowrap">
              {display}
            </Text>
            <SecureActions
              copyValue={copyValue}
              isVisible={isVisible}
              onToggle={() => setVisibleMap((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }))}
              copyLabel="Copy card number"
              toggleLabels={{ show: 'Show card number', hide: 'Hide card number' }}
            />
          </HStack>
        );
      },
    }),
    columnHelper.accessor('credentials.expiration', {
      id: 'credentials.expiration',
      header: () => headerCell(t('EXPIRY')),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue() || 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('credentials.cvv', {
      id: 'credentials.cvv',
      header: () => headerCell(t('CVV')),
      cell: (info) => {
        const val = info.getValue();
        if (!val) {
          return <NAMono />;
        }
        const rowKey = getRowKey(info);
        const isVisible = !!visibleCvvMap[rowKey];
        const cvv = String(val).trim();
        const display = isVisible ? cvv : '•'.repeat(cvv.length || 3);
        return (
          <HStack justify="space-between" spacing={2} align="center">
            <Text color={textColor} fontSize="sm" fontWeight="700" fontFamily="monospace" noOfLines={1}>
              {display}
            </Text>
            <SecureActions
              copyValue={cvv}
              isVisible={isVisible}
              onToggle={() => setVisibleCvvMap((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }))}
              copyLabel="Copy CVV"
              toggleLabels={{ show: 'Show CVV', hide: 'Hide CVV' }}
            />
          </HStack>
        );
      },
    }),
    columnHelper.accessor('available_balance', {
      id: 'available_balance',
      header: () => headerCell(t('AVAILABLE BALANCE')),
      cell: (info) => {
        const value = info.getValue();
        const currency = info.row.original.currency || 'N/A';
        return (
          <Text color={textColor} textAlign="center" fontSize="sm" fontWeight="700">
            {currency} {value !== undefined && value !== null ? Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : t('N/A')}
          </Text>
        );
      },
    }),
    columnHelper.accessor('is_active', {
      id: 'is_active',
      header: () => headerCell(t('STATUS')),
      cell: (info) => {
        const val = info.getValue();
        let colorScheme = 'gray';
        let label = t('N/A');
        if (val === true) {
          colorScheme = 'green';
          label = t('Active');
        } else if (val === false) {
          colorScheme = 'red';
          label = t('Inactive');
        } else if (val === null) {
          colorScheme = 'orange';
          label = t('Blocked');
        }
        return (
          <Flex justify="center">
            <Button
              colorScheme={colorScheme}
              size="xs"
              px="3"
              py="1"
              minW="0"
              isDisabled
              _disabled={{ opacity: 1, bg: `${colorScheme}.500`, color: 'white', cursor: 'default' }}
            >
              {label}
            </Button>
          </Flex>
        );
      },
    }),
    columnHelper.display({
      id: 'action',
      header: () => headerCell(t('ACTION')),
      cell: (info) => {
        const row = info?.row?.original || {};
        const { prefund, is_active: isActive, provider, card_id, currency } = row;
        const settings = currencies?.find((item) => item.currency_short === currency) || {};
        
        return (
          <Flex direction="column" align="center" gap={1}>
            {prefund && isActive && (
              <>
                <Button
                  colorScheme="brand"
                  size="xs"
                  onClick={() => {
                    setPostData({
                      action: 'fund_virtual_card', provider, prefund: settings?.prefund,
                      min: settings?.card_minimum_top_up, max: settings?.card_maximum_top_up,
                      currency_short: currency, card_id, currency, ...clearPricingFields({ amount: 0 }),
                    });
                    setError([]);
                    onActionModalOpen();
                  }}
                >
                  {t('Fund')}
                </Button>
                <Button
                  colorScheme="gray"
                  variant="outline"
                  size="xs"
                  onClick={() => {
                    setPostData({
                      action: 'withdraw_virtual_card', provider, prefund: settings?.prefund,
                      min: settings?.card_minimum_top_up, max: settings?.card_maximum_top_up,
                      currency_short: currency, card_id, currency, ...clearPricingFields({ amount: 0 }),
                    });
                    setError([]);
                    onActionModalOpen();
                  }}
                >
                  {t('Withdraw')}
                </Button>
              </>
            )}
            <Button
              colorScheme="brand"
              variant="outline"
              size="xs"
              onClick={() => {
                navigate(`/admin/card-transactions/${card_id}`);
              }}
            >
              {t('History')}
            </Button>
          </Flex>
        );
      },
    }),
  ];
  const [data, setData] = React.useState([...tableData]);
  
  // Filter data based on search query
  const filteredData = React.useMemo(() => {
    if (!searchQuery || searchQuery.trim() === "") {
      return data;
    }
    const query = searchQuery.toLowerCase().trim();
    return data.filter((card) => {
      const cardName = card.card_name?.toLowerCase() || "";
      const cardNumber = card.card_number?.toLowerCase() || "";
      const cardStatus = card.status?.toLowerCase() || "";
      const cardType = card.card_type?.toLowerCase() || "";
      const currency = card.currency?.toLowerCase() || "";
      
      return cardName.includes(query) || cardNumber.includes(query) || cardStatus.includes(query) || cardType.includes(query) || currency.includes(query);
    });
  }, [data, searchQuery]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
      pagination: { pageSize, pageIndex },
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPagination,
    setPageSize: pageSize,
    getPaginationRowModel: getPaginationRowModel(),
    debugTable: true,
  });

  React.useEffect(() => {
    setData([...tableData]);
  }, [tableData, table]);

  const getNextPage = () => {
    table.nextPage();
    setTimeout(() => index(table.getState().pagination.pageIndex + 1), 0);
  };

  const getPreviousPage = () => {
    table.previousPage();
    setTimeout(() => index(table.getState().pagination.pageIndex + 1), 0);
  };

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Flex px="25px" mb="8px" justifyContent="space-between" align="center">
        <Text
          color={textColor}
          fontSize="22px"
          mb="4px"
          fontWeight="700"
          lineHeight="100%"
        >
          {t('Payment Cards')}
        </Text>
        <Button
          colorScheme="brand"
          size="sm"
          leftIcon={<AddIcon />}
          onClick={() => {
            setPostData({
              action: 'create_virtual_card',
              ...clearPricingFields(),
            });
            setError([]);
            onModalOpen();
          }}
        >
          {t('Create Card')}
        </Button>
      </Flex>
      {/* Allow only horizontal scrolling within the table container; let the page handle vertical flow */}
      <Box overflowX="auto">
        {loading ? (
          <Flex justifyContent="center" alignItems="center" height="200px">
            <Spinner size="xl" />
          </Flex>
        ) : data.length === 0 ? (
          <Flex justifyContent="center" alignItems="center" height="200px">
            <Text color={textColor} fontSize="sm" fontWeight="500">
              {t('No results found! Check')} <NavLink style={{ color: 'green' }} to={constants.urls.documentation} >{t('API documentation')}</NavLink> {t('for more details')}
            </Text>
          </Flex>
        ) : (
          <Table variant="simple" color="gray.500" mb="24px" mt="12px" minW={{ base: '900px', md: '1000px' }}>
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <Th
                        key={header.id}
                        colSpan={header.colSpan}
                        pe="10px"
                        borderColor={borderColor}
                        cursor="pointer"
                        textAlign="center"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <Flex
                          justifyContent="center"
                          align="center"
                          fontSize={{ sm: '10px', lg: '12px' }}
                          color="gray.400"
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </Flex>
                      </Th>
                    );
                  })}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table
                .getRowModel()
                .rows.slice(0, 11)
                .map((row) => {
                  return (
                    <Tr key={row.id}>
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <Td
                            key={cell.id}
                            fontSize={{ sm: '14px' }}
                            minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                            borderColor="transparent"
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </Td>
                        );
                      })}
                    </Tr>
                  );
                })}
            </Tbody>
          </Table>
        )}
      </Box>
      <Flex justifyContent="space-between" alignItems="center" px="25px" py="10px">
        <Button onClick={getPreviousPage} isDisabled={!table.getCanPreviousPage()}>
          {t('Previous')}
        </Button>
        <Text>
          {t('Page')} {table.getState().pagination.pageIndex + 1} {t('of')} {table.getPageCount()}
        </Text>
        <Button onClick={getNextPage} isDisabled={!table.getCanNextPage()}>
          {t('Next')}
        </Button>
        <Select
          value={pageSize}
          width="100px"
          onChange={(e) => {
            setPagination((prev) => ({
              ...prev,
              pageSize: Number(e.target.value),
            }));
          }}
        >
          {sortArray.map((size) => (
            <option key={size} value={size}>
              {t('Show')} {size} {t('Max')}
            </option>
          ))}
        </Select>
      </Flex>

      {/* Fund/withdraw card */}
      <Modal isOpen={isActionModalOpen} onClose={onActionModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {
              postData?.action === 'fund_virtual_card' ? t('Fund') : t('Withdraw')
            }
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {
              postData?.currency && postData?.action === 'fund_virtual_card' ? (
                <>
                  <select
                    required
                    onChange={(e) => setPostData((prev) => ({
                      ...prev,
                      debit_currency: e.target.value,
                      country_currency_short: e.target.value,
                      ...clearPricingFields(),
                    }))}
                    value={postData?.debit_currency || ''}
                    style={baseSelectStyle}
                  >
                    <option value="" disabled>{t('Select')} {t('Debit')} {t('Fiat Wallet')}</option>
                    {
                      wallets && wallets.map((item, i) => (
                        <option key={i} value={item.country_currency_short}>{item.country_currency_short} - {item.country_currency_symbol} {money(item?.available_balance || 0)}</option>
                      ))
                    }
                  </select>
                </>
              ) : null
            }

            <Input
              isRequired={true}
              variant='auth'
              fontSize='sm'
              value={postData?.amount || ''}
              type={'number'}
              ms={{ base: "0px", md: "0px" }}
              onChange={e => {
                setPostData((prev) => ({
                  ...prev,
                  ...clearPricingFields({ amount: e?.target?.value })
                }))
              }}
              placeholder={t('Amount') + ` in ${postData?.currency}`}
              mb='10px'
              fontWeight='500'
              size='lg'
            />

            {postData?.conversion_id && postData?.fee_id && (
              <Text fontSize='xs' color={'green'} ml='5px'>
                {t('You would be charged a total of')} {money(postData?.total)} {postData?.sub_currency || t('N/A')}. {t('This includes a fee of')} {money(postData?.fee)} {postData?.fee_currency || t('N/A')}
              </Text>
            )}

            {
              error.length > 0 && (
                error.map((err, index) => (
                  <Text
                    key={index}
                    mt='10px'
                    ms='4px'
                    color={'red'}
                    fontWeight='400'
                    fontSize='sm'>
                    * {err.msg}
                  </Text>
                ))
              )
            }
          </ModalBody>
          <ModalFooter>
            <Button
              me='100%'
              mb='10px'
              w='140px'
              fontSize={'sm'}
              mr={3} onClick={onActionModalClose}
              minW='140px'
              mt={{ base: "20px", "2xl": "auto" }}
              variant='ghost'
              fontWeight='500'>
              {t('Close')}
            </Button>
            {
              !postData?.conversion_id && !postData?.fee_id ? (
                <Button
                  isLoading={loading}
                  isDisabled={loading}
                  me='100%'
                  mb='10px'
                  w='140px'
                  fontSize={'sm'}
                  mr={1} onClick={() => {
                    return getConversion(postData?.action, postData?.amount, postData?.currency, postData?.action == 'fund_virtual_card' ? postData?.debit_currency : postData?.currency, postData?.provider, postData)
                  }}
                  minW='140px'
                  mt={{ base: "20px", "2xl": "auto" }}
                  variant='brand'
                  fontWeight='500'>
                  {t('Verify')}
                </Button>
              ) : (
                postData?.action === 'fund_virtual_card' ? (
                  <Button
                    isLoading={loading}
                    isDisabled={loading}
                    me='100%'
                    mb='10px'
                    w='140px'
                    mr={1} onClick={() => {
                      return fundCard(postData?.action, postData?.card_id, postData)
                    }}
                    fontSize={'sm'}
                    minW='200px'
                    mt={{ base: "20px", "2xl": "auto" }}
                    variant='brand'
                    fontWeight='500'>
                    {t('Fund')} {postData?.currency} {money(postData?.amount || 0)}
                  </Button>
                ) : postData?.action === 'withdraw_virtual_card' ? (
                  <Button
                    isLoading={loading}
                    isDisabled={loading}
                    me='100%'
                    mb='10px'
                    w='140px'
                    fontSize={'sm'}
                    mr={1} onClick={() => {
                      return withdrawFromCard(postData?.action, postData?.card_id, postData)
                    }}
                    minW='160px'
                    mt={{ base: "20px", "2xl": "auto" }}
                    variant='brand'
                    fontWeight='500'>
                    {t('Withdraw')} {postData?.currency} {money(postData?.amount || 0)}
                  </Button>
                ) : null
              )
            }
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Create card */}
      <Modal isOpen={isModalOpen} onClose={onModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('Create Card')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <select
              required
              onChange={(e) => {
                const filter = currencies?.find((item) => item.currency_short === e?.target?.value) || {};
                setPostData((prev) => ({
                  ...prev,
                  currency: e?.target?.value,
                  currency_short: e?.target?.value,
                  min: filter?.card_minimum_top_up,
                  max: filter?.card_maximum_top_up,
                  prefund: filter?.prefund,
                  brands: filter?.brands || [],
                  types: filter?.types || [],
                  brand: null,
                  provider: null,
                  ...clearPricingFields({ amount: 0 })
                }));
              }}
              value={postData?.currency || ''}
              style={baseSelectStyle}
            >
              <option value="" disabled>{t('Select Card Currency')}</option>
              {
                currencies && currencies.map((item, i) => (
                  <option key={i} value={item.currency_short}>{item.currency_short} - {item.currency_symbol}</option>
                ))
              }
            </select>
            {
              postData?.currency ? (
                <>
                  <select
                    required
                    onChange={(e) => {
                      const filter = postData?.brands?.find((item) => item.value === e?.target?.value) || {};
                      setPostData((prev) => ({
                        ...prev,
                        brand: e.target.value,
                        provider: filter?.provider,
                        instructions: filter?.instructions,
                        ...clearPricingFields({ amount: 0 })
                      }))
                    }}
                    value={postData?.brand || ''}
                    style={baseSelectStyle}
                  >
                    <option value="" disabled>{t('Choose Card Brand')}</option>
                    {
                      postData?.brands && postData.brands.map((item, i) => (
                        <option key={i} value={item.value}>{item.name}</option>
                      ))
                    }
                  </select>
                  <select
                    required
                    onChange={(e) => {
                      setPostData((prev) => ({
                        ...prev,
                        type: e.target.value,
                        ...clearPricingFields({ amount: 0 })
                      }))
                    }}
                    value={postData?.type || ''}
                    style={baseSelectStyle}
                  >
                    <option value="" disabled>{t('Select Card Type')}</option>
                    {
                      postData?.types && postData?.types.map((item, i) => (
                        <option key={i} value={item.value}>{item.name}</option>
                      ))
                    }
                  </select>
                </>
              ) : null
            }
            {
              postData?.currency ? (
                <>
                  <select
                    required
                    onChange={(e) => setPostData((prev) => ({
                      ...prev,
                      debit_currency: e.target.value,
                      country_currency_short: e.target.value,
                      ...clearPricingFields({ amount: 0 })
                    }))}
                    value={postData?.debit_currency || ''}
                    style={baseSelectStyle}
                  >
                    <option value="" disabled>{t('Select Debit Fiat Wallet')}</option>
                    {
                      wallets && wallets.map((item, i) => (
                        <option key={i} value={item.country_currency_short}>{item.country_currency_short} - {item.country_currency_symbol} {item?.available_balance ? item?.available_balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 0}</option>
                      ))
                    }
                  </select>
                </>
              ) : null
            }
            {
              postData?.instructions && (
                <Box
                  bg={bannerBg}
                  border="1px"
                  borderColor={bannerBorder}
                  borderLeftWidth="4px"
                  borderLeftColor={bannerAccent}
                  rounded="md"
                  px="3"
                  py="2"
                  mb="20px"
                >
                  <HStack align="start" spacing={3}>
                    <InfoOutlineIcon color={bannerAccent} mt="1" />
                    <Box>
                      <Text fontSize="sm" fontWeight="700" color={textColor} mb="1">{t('Instructions')}</Text>
                      <Text fontSize='xs' color={instructionsBodyColor}>
                        {postData?.instructions}
                      </Text>
                    </Box>
                  </HStack>
                </Box>
              )
            }
            {
              postData?.debit_currency && postData?.prefund == true ? (
                <>
                  <Input
                    isRequired={true}
                    variant='auth'
                    fontSize='sm'
                    value={postData?.amount || ''}
                    type={'number'}
                    ms={{ base: "0px", md: "0px" }}
                    onChange={e => {
                      setPostData((prev) => ({
                        ...prev,
                        ...clearPricingFields({ amount: e?.target?.value })
                      }))
                    }}
                    placeholder={t('Prefund Amount') + ` in ${postData?.currency}`}
                    mb='24px'
                    fontWeight='500'
                    size='lg'
                  />
                </>
              ) : (
                null
              )
            }
            <Input
              isRequired={true}
              variant='auth'
              fontSize='sm'
              value={postData?.name_on_card || ''}
              type={'text'}
              ms={{ base: "0px", md: "0px" }}
              onChange={e => setPostData((prev) => ({
                ...prev,
                name_on_card: e?.target?.value
              }))}
              placeholder={t('Name on Card')}
              mb='10px'
              fontWeight='500'
              size='lg'
            />

            {postData?.conversion_id && postData?.fee_id && (
              <Text fontSize='xs' color={'green'} ml='5px'>
                {t('You would be charged a total of')} {money(postData?.total)} {postData?.country_currency_short || t('N/A')}. {t('This includes a card creation fee of')} {money(postData?.fee)} {postData?.fee_currency || t('N/A')}
              </Text>
            )}

            {
              error.length > 0 && (
                error.map((err, index) => (
                  <Text
                    key={index}
                    mt='10px'
                    ms='4px'
                    color={'red'}
                    fontWeight='400'
                    fontSize='sm'>
                    * {err.msg}
                  </Text>
                ))
              )
            }
          </ModalBody>

          <ModalFooter>
            <Button
              me='100%'
              mb='10px'
              w='140px'
              fontSize={'sm'}
              mr={3} onClick={onModalClose}
              minW='140px'
              mt={{ base: "20px", "2xl": "auto" }}
              variant='ghost'
              fontWeight='500'>
              {t('Close')}
            </Button>
            {
              postData?.conversion_id && postData?.fee_id ? (
                <Button
                  isLoading={loading}
                  isDisabled={loading}
                  me='100%'
                  mb='10px'
                  w='140px'
                  mr={1} onClick={() => {
                    return createCard(postData?.action, postData)
                  }}
                  fontSize={'sm'}
                  minW='200px'
                  mt={{ base: "20px", "2xl": "auto" }}
                  variant='brand'
                  fontWeight='500'>
                  {t('Pay')} {money(postData?.total)} {postData?.country_currency_short || t('N/A')}
                </Button>
              ) : (
                <Button
                  isLoading={loading}
                  isDisabled={loading}
                  me='100%'
                  mb='10px'
                  w='140px'
                  fontSize={'sm'}
                  mr={1} onClick={() => {
                    return getConversion(postData?.action, postData?.amount, postData?.currency, postData?.debit_currency, postData?.provider, postData)
                  }}
                  minW='140px'
                  mt={{ base: "20px", "2xl": "auto" }}
                  variant='brand'
                  fontWeight='500'>
                  {t('Verify')}
                </Button>
              )
            }
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );

  // Get conversion
  async function getConversionRates(amount, from_currency, to_currency, provider = null) {
    if (!from_currency || !to_currency || !provider) {
      constants.showToast(
        "error",
        t("FAILED"),
        t("Snap! All fields are required")
      );
      throw new Error('Missing required fields');
    }
    return new Promise((resolve, reject) => {
      endpoints.getConversionRates(
        amount,
        from_currency,
        to_currency,
        'selling_price',
        provider,
        null,
        null,
        (error, data) => {
          if (error) {
            reject(error);
          } else {
            resolve({
              conversion_id: data?.conversion_id,
              exchange_rate: Number(data?.exchange_rate),
              sub_currency: data?.currency,
              sub_total: Number(data?.exchange_rate) * Number(quantity),
            });
          }
        }
      );
    });
  }

  // Get virtual card processing fees
  async function getVirtualCardProcessingFee(identifier, amount, currency, debit_currency, provider, type) {
    if (!identifier || !currency || !debit_currency || !provider || !type) {
      constants.showToast(
        "error",
        t("FAILED"),
        t("Snap! All fields are required")
      );
      throw new Error('Missing required fields');
    }
    return new Promise((resolve, reject) => {
      endpoints.getFees(identifier, amount, currency, debit_currency, provider, type, null, (error, data) => {
        if (error) {
          reject(error);
        } else {
          resolve({
            fee_id: data?.fee_id,
            fee_currency: data?.currency,
            fee: Number(data?.fee) * Number(quantity)
          });
        }
      });
    });
  }

  // Get conversion rates
  async function getConversion(identifier, amount, from_currency, to_currency, provider, data) {
    try {
      if (!validateInputs(from_currency, amount, data)) return;
      setLoading(true);
      const conv = await getConversionRates(amount, from_currency, to_currency, provider);
      const fee = await getVirtualCardProcessingFee(identifier, amount, from_currency, to_currency, provider, identifier);
      setPostData((prev) => ({
        ...prev, ...conv, ...fee,
        total: Number(conv?.sub_total || 0) + Number(fee?.fee || 0),
      }));
    } catch (error) {
      setError(error?.errors || []);
      constants.showToast(
        "error",
        t("FAILED"),
        error?.message || t("Snap! Something went wrong, Try again")
      );
    } finally {
      setLoading(false);
    }
  }

  // Create card
  async function createCard(identifier, postData) {
    const create_bill = {
      currency: postData?.currency, amount: postData?.amount, brand: postData?.brand, quantity: quantity, identifier: identifier,
      debit_currency: postData?.debit_currency, name_on_card: postData?.name_on_card, conversion_id: postData?.conversion_id,
      type: postData?.type, provider: postData?.provider, fee_id: postData?.fee_id, prefund: postData?.prefund,
    }
    if (!confirmValidation(create_bill)) { return }
    setLoading(true);
    endpoints.createCard(create_bill, (error, data) => {
      setLoading(false);
      if (error) {
        setError(error.errors);
        constants.showToast(
          "error",
          t("FAILED"),
          error?.message || t("Snap! Something went wrong, Try again")
        );
      } else {
        constants.showToast(
          "success",
          t("SUCCESS"),
          data?.message || t("Card created successfully!")
        );
        onModalClose();
        window.location.reload();
      }
    });
  }

  // Fund card
  async function fundCard(identifier, card_id, postData) {
    const create_bill = {
      card_id, currency: postData?.currency, amount: postData?.amount, conversion_id: postData?.conversion_id,
      debit_currency: postData?.debit_currency, identifier: identifier, fee_id: postData?.fee_id,
      type: identifier, provider: postData?.provider, quantity: quantity,
    }
    if (!confirmValidation(create_bill)) { return }
    setLoading(true);
    endpoints.fundCard(create_bill, (error, data) => {
      setLoading(false);
      if (error) {
        setError(error.errors);
        constants.showToast(
          "error",
          t("FAILED"),
          error?.message || t("Snap! Something went wrong, Try again")
        );
      } else {
        constants.showToast(
          "success",
          t("SUCCESS"),
          data?.message || t("Card funded successfully!")
        );
        onActionModalClose();
        window.location.reload();
      }
    });
  }

  // Withdraw from card
  async function withdrawFromCard(identifier, card_id, postData) {
    const create_bill = {
      card_id, currency: postData?.currency, amount: postData?.amount, conversion_id: postData?.conversion_id,
      identifier: identifier, fee_id: postData?.fee_id, type: identifier, provider: postData?.provider,
    }
    if (!confirmValidation(create_bill)) { return }
    setLoading(true);
    endpoints.withdrawFromCard(create_bill, (error, data) => {
      setLoading(false);
      if (error) {
        setError(error.errors);
        constants.showToast(
          "error",
          t("FAILED"),
          error?.message || t("Snap! Something went wrong, Try again")
        );
      } else {
        constants.showToast(
          "success",
          t("SUCCESS"),
          data?.message || t("Card withdrawn successfully!")
        );
        onActionModalClose();
        window.location.reload();
      }
    });
  }

  // Confirm validation
  function confirmValidation(create_card) {
    if (create_card?.prefund == true && !create_card?.amount || !create_card?.provider || !create_card?.type || !create_card?.currency) {
      constants.showToast('error', t('ERROR'), t('All fields are required!'))
      return false;
    }
    return true;
  }

  // Validate inputs
  function validateInputs(currency, amount, data) {
    if (data?.prefund == true && !amount || data?.prefund == true && isNaN(Number(amount)) || data?.prefund == true && Number(amount) <= 0) {
      constants.showToast('error', t('ERROR'), t('Oops! Invalid amount'))
      return false;
    }
    if (data?.prefund == true && Number(amount) < Number(data?.min)) {
      constants.showToast('error', t('ERROR'), t('Minimum amount is') + ' ' + data?.min + ' ' + currency)
      return false;
    }
    if (data?.prefund == true && Number(amount) > Number(data?.max)) {
      constants.showToast('error', t('ERROR'), t('Maximum amount is') + ' ' + data?.max + ' ' + currency)
      return false;
    }
    return true;
  }
}
